import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, X } from 'lucide-react';
import { useSelection } from '../contexts/SelectionContext';

/**
 * Compact pill that surfaces selections persisted across views. Shows total
 * count + per-view breakdown (e.g. "5 selected · Inbox 3 · Payments 2") and
 * a one-click "Clear all". Hidden when nothing is selected. Clicking a view
 * chip jumps to that view.
 */

const VIEW_META = {
  inbox: { label: 'Inbox', path: '/inbox' },
  calls: { label: 'Calls', path: '/calls' },
  reading: { label: 'Reading', path: '/reading' },
  payments: { label: 'Payments', path: '/payments' },
};

export default function GlobalSelectionIndicator() {
  const { store, clearAll, clearSelection } = useSelection();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const summary = useMemo(() => {
    const entries = Object.entries(store || {})
      .map(([key, entry]) => ({ key, count: entry?.ids?.length || 0 }))
      .filter((e) => e.count > 0);
    const total = entries.reduce((n, e) => n + e.count, 0);
    return { entries, total };
  }, [store]);

  if (!summary.total) return null;

  const handlePillClick = () => setOpen((prev) => !prev);
  const handleClearAll = (e) => {
    e.stopPropagation();
    clearAll();
    setOpen(false);
  };
  const handleViewChip = (key) => {
    const meta = VIEW_META[key];
    if (meta) navigate(meta.path);
    setOpen(false);
  };
  const handleViewClear = (e, key) => {
    e.stopPropagation();
    clearSelection(key);
  };

  return (
    <div className="relative">
      <button
        data-testid="global-selection-indicator"
        onClick={handlePillClick}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors"
        style={{
          background: 'var(--pd-focus)',
          color: 'white',
          border: '1px solid var(--pd-focus)',
        }}
        title="Persisted selections across views"
      >
        <ListChecks className="w-3 h-3" />
        <span data-testid="global-selection-total">{summary.total}</span>
        <span className="opacity-80">selected</span>
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-[150]" onClick={() => setOpen(false)} />
          <div
            data-testid="global-selection-popover"
            className="absolute right-0 top-9 w-64 rounded-2xl py-2 z-[151] animate-fade-in"
            style={{
              background: 'var(--pd-popover)',
              border: '1px solid var(--pd-popover-border)',
              boxShadow: 'var(--pd-shadow-3)',
            }}
          >
            <div className="px-3 pb-2" style={{ borderBottom: '1px solid var(--pd-border)' }}>
              <div className="text-[11px] font-semibold" style={{ color: 'var(--pd-text)' }}>
                Persisted selections
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>
                Items stay selected when you navigate between views.
              </div>
            </div>
            <div className="py-1">
              {summary.entries.map(({ key, count }) => {
                const meta = VIEW_META[key] || { label: key, path: '/' };
                return (
                  <div
                    key={key}
                    data-testid={`global-selection-row-${key}`}
                    className="flex items-center px-3 py-1.5 hover:bg-black/[0.03] transition-colors"
                  >
                    <button
                      onClick={() => handleViewChip(key)}
                      className="flex-1 flex items-center justify-between text-left"
                    >
                      <span className="text-[12px] font-medium" style={{ color: 'var(--pd-text)' }}>
                        {meta.label}
                      </span>
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-muted)' }}
                      >
                        {count}
                      </span>
                    </button>
                    <button
                      data-testid={`global-selection-clear-${key}`}
                      onClick={(e) => handleViewClear(e, key)}
                      className="ml-2 p-1 rounded-full hover:bg-black/[0.05]"
                      title={`Clear ${meta.label} selection`}
                    >
                      <X className="w-3 h-3" style={{ color: 'var(--pd-subtle)' }} />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="px-3 pt-1.5 pb-0.5" style={{ borderTop: '1px solid var(--pd-border)' }}>
              <button
                data-testid="global-selection-clear-all"
                onClick={handleClearAll}
                className="w-full text-left text-[11px] font-medium py-1.5 rounded-lg hover:bg-black/[0.03] px-2"
                style={{ color: 'var(--pd-crimson)' }}
              >
                Clear all selections
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
